import React from "react"
import { Layout } from "../components/Layout"
import { Cell } from "baseui/layout-grid"
import { ParagraphLarge } from "baseui/typography"
import { Person } from "../App"
import { findBranch, getDollarsAndCents } from "../util"
import { getConstant } from "../constants"

const ContactParagraph = () => (
  <ParagraphLarge>
    If you have any questions about the join process, you can contact the
    Australian Unions Support Centre (Monday-Friday 9am-7pm Melbourne) on{" "}
    <a href="tel:1300 486 466">1300 486 466</a>, or by email -{" "}
    <a href="mailto:help@actu.org.au">help@actu.org.au</a>
  </ParagraphLarge>
)
const ContactStatusParagraph = () => (
  <ParagraphLarge>
    You can check on progress by contacting the Australian Unions Support Centre
    (Monday-Friday 9am-7pm Melbourne) on{" "}
    <a href="tel:1300 486 466">1300 486 466</a>, or by email -{" "}
    <a href="mailto:help@actu.org.au">help@actu.org.au</a>
  </ParagraphLarge>
)

const ReferenceParagraph = ({ reference }: { reference: string }) => (
  <ParagraphLarge>
    Your union join reference number* is: {reference}
  </ParagraphLarge>
)
const NotMemberNumberParagraph = ({ union }: { union?: string }) => (
  <ParagraphLarge>
    <em>
      *Please note, this is not your member number.
      {union ? ` Your member number will be provided by the ${union}.` : ""}
    </em>
  </ParagraphLarge>
)
const SummaryParagraph = () => (
  <ParagraphLarge>
    A summary of this message has been emailed to you.
  </ParagraphLarge>
)

export const CompletePage = ({
  person,
  setPerson,
  resetState,
  volunteerOnly
}: {
  person: Person
  setPerson: (person: Person | ((oldPerson: Person) => Person)) => void
  resetState: () => void
  volunteerOnly: boolean
}) => {
  React.useEffect(() => {
    setPerson(oldPerson => {
      return {
        ...oldPerson,
        people_id: undefined
      }
    })
    return resetState
    // setPerson omitted because
    // React guarantees that setState function identity is stable and won’t change on re-renders.
    // This is why it’s safe to omit from the useEffect or useCallback dependency list.
    // https://reactjs.org/docs/hooks-reference.html
    // eslint-disable-next-line
  }, [resetState])
  const branch = findBranch(person)
  let step = 3
  if (person.join) {
    if (branch && branch.payment_mode === "bypass") {
      step = step + 1
    } else {
      step = step + 2
    }
  }
  if (person.volunteer && person.join) {
    step++
  }
  let completeMessage: JSX.Element | null = null
  const { dollars, cents } = getDollarsAndCents(person.cost)
  const reference = person.person_id || ""
  if (!volunteerOnly) {
    if (branch) {
      if (branch.payment_mode === "bypass") {
        // Scenario A on the JIRA ticket (RG-149)
        completeMessage = (
          <>
            <ParagraphLarge>
              To join {branch.short_name} please complete the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={branch.website}
              >
                membership application form on their website
              </a>
              .
            </ParagraphLarge>
            <ContactParagraph />
            <ReferenceParagraph reference={reference} />
            <SummaryParagraph />
            <NotMemberNumberParagraph />
          </>
        )
      } else if (branch.payment_mode === "stored") {
        // Scenario D on the JIRA ticket (RG-149)
        completeMessage = (
          <>
            <ParagraphLarge>
              Congratulations on joining the {branch.short_name}! Your details
              have been sent to them and they'll be in touch soon.
            </ParagraphLarge>
            {person.payment_type === "dd" ? (
              <>
                <ParagraphLarge>
                  Your first payment of ${dollars}.{cents} will be processed by
                  your union via Direct Debit for the first month of your
                  membership. After this the {branch.short_name} will move you
                  onto their fee rate.
                </ParagraphLarge>
                <ParagraphLarge>
                  A copy of your Direct Debit Service Agreement with the{" "}
                  {branch.short_name} has been emailed to you for your records.
                </ParagraphLarge>
              </>
            ) : (
              <ParagraphLarge>
                Your union will be in touch shortly to collect your payment
                details and complete your membership application.
              </ParagraphLarge>
            )}
            <ContactParagraph />
            <ReferenceParagraph reference={reference} />
            <SummaryParagraph />
            <NotMemberNumberParagraph union={branch.short_name} />
          </>
        )
      } else if (branch.payment_mode === "fatzebra") {
        // Scenario C on the JIRA ticket (RG-149)
        completeMessage = (
          <>
            <ParagraphLarge>
              Congratulations on joining the {branch.short_name}! Your details
              have been sent to them and they'll be in touch soon.
            </ParagraphLarge>
            <ParagraphLarge>
              Your first payment of ${dollars}.{cents} has been{" "}
              {person.payment_type === "cc" ? "processed" : "scheduled"} via{" "}
              {person.payment_type === "cc" ? "credit card" : "direct debit"}{" "}
              for the first month of your membership. After this, the{" "}
              {branch.short_name} will move you onto their fee rate.
            </ParagraphLarge>
            {person.payment_type === "dd" && (
              <ParagraphLarge>
                A copy of your Direct Debit Service Agreement with the{" "}
                {branch.short_name} has been emailed to you for your records.
              </ParagraphLarge>
            )}
            <ContactParagraph />
            <ReferenceParagraph reference={reference} />
            <SummaryParagraph />
            <NotMemberNumberParagraph union={branch.short_name} />
          </>
        )
      }
    } else if (getConstant("xmasBreakEnabled")) {
      // Only applies in xmas break when env var is turned on
      completeMessage = (
        <>
          <ParagraphLarge>
            Please note that our office is closed from 24 December - 4 January.
            We will be in touch in early January to confirm your union and
            complete the joining process.
          </ParagraphLarge>
          <ParagraphLarge>
            You can contact the Australian Unions Support Centre (Monday-Friday
            9am-7pm Melbourne) on <a href="tel:1300 486 466">1300 486 466</a>,
            or by email - <a href="mailto:help@actu.org.au">help@actu.org.au</a>{" "}
            - from 9am AEDT, Tuesday 5 January 2020.
          </ParagraphLarge>
          {person.payment_type === "dd" && (
            <ParagraphLarge>
              A copy of your Direct Debit Service Agreement has been emailed to
              you for your records. An updated Direct Debit Service Agreement
              will be sent to you once your union is confirmed.
            </ParagraphLarge>
          )}
          <ReferenceParagraph reference={reference} />
          <SummaryParagraph />
          <NotMemberNumberParagraph />
        </>
      )
    } else {
      // Scenario B on the JIRA ticket (RG-149)
      // Manual confirm
      completeMessage = (
        <>
          <ParagraphLarge>
            We'll confirm your union with you shortly and send them your
            details. Please note, payment will only be taken at that stage.
          </ParagraphLarge>
          {person.payment_type === "dd" && (
            <ParagraphLarge>
              A copy of your Direct Debit Service Agreement has been emailed to
              you for your records. An updated Direct Debit Service Agreement
              will be sent to you once your union is confirmed.
            </ParagraphLarge>
          )}
          <ContactStatusParagraph />
          <ReferenceParagraph reference={reference} />
          <SummaryParagraph />
          <NotMemberNumberParagraph />
        </>
      )
    }
  } else {
    completeMessage = (
      <>
        <ParagraphLarge>
          {volunteerOnly
            ? "Thanks for signing up."
            : "Welcome to Australian Unions"}
        </ParagraphLarge>
        {volunteerOnly ? (
          <ParagraphLarge>
            We'll be in touch soon with ways you can volunteer. In the meantime,{" "}
            <a href="https://www.australianunions.org.au/campaigns">
              check out the campaigns we are currently running.
            </a>
          </ParagraphLarge>
        ) : (
          <ParagraphLarge>
            We'll be in touch shortly to complete the joining process.
          </ParagraphLarge>
        )}
        <ParagraphLarge>
          Please also consider making a{" "}
          <a href="https://www.australianunions.org.au/donate">donation</a> to
          help make the union movement stronger.
        </ParagraphLarge>
      </>
    )
  }
  return (
    <Layout
      heading="Thanks for joining the movement."
      sectionStep={step}
      person={person}
    >
      <Cell span={[4, 8, 12]}>{completeMessage}</Cell>
    </Layout>
  )
}
